import {
  FileUploadStatus,
  Label,
  SingleCombobox,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { LegacyDocumentType } from '@prisma/client';

type TServiceFile = {
  id: number;
  fileName: string | null;
};

type TSelectDocumentStepProps = {
  isServiceRequest: boolean;
  serviceFiles: TServiceFile[];
  selectedFileIds: number[];
  companyId: number | null | undefined;
  uploadFile: FileBasic | null;
  isUploading: boolean;
  onSelectFileIds: (ids: number[]) => void;
  onFileSet: (file: FileBasic | null) => void;
  onUploadStatusChange?: (status: FileUploadStatus) => void;
};

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/msword': ['.doc'],
  'image/*': ['.png', '.jpg', '.jpeg'],
} as const;

const MAX_SIZE = 10 * 1024 * 1024;

export function SelectDocumentStep({
  isServiceRequest,
  serviceFiles,
  selectedFileIds,
  companyId,
  uploadFile,
  isUploading,
  onSelectFileIds,
  onFileSet,
  onUploadStatusChange,
}: TSelectDocumentStepProps) {
  if (isServiceRequest) {
    return (
      <>
        <div className="flex flex-col gap-2">
          <Label className="txt-small-medium text-text-secondary">
            Documents on this service
          </Label>
          {serviceFiles.length > 0 ? (
            <SingleCombobox
              options={serviceFiles.map((f) => ({
                value: String(f.id),
                label: f.fileName ?? '',
              }))}
              value={
                selectedFileIds[0] != null ? String(selectedFileIds[0]) : ''
              }
              setValue={(v) => {
                onSelectFileIds(v ? [Number(v)] : []);
              }}
              placeholder="Search and select a document..."
              searchPlaceholder="Search documents..."
              notFoundText="No documents match."
            />
          ) : companyId == null ? (
            <p className="txt-small-regular text-text-secondary">
              No documents available on this service request.
            </p>
          ) : (
            <p className="txt-small-regular text-text-secondary">
              No documents on this service yet. Upload a file below to send for
              signature.
            </p>
          )}
        </div>

        {companyId != null && (
          <div className="flex flex-col gap-2">
            <Label className="txt-small-medium text-text-secondary">
              Or upload a new file
            </Label>
            <SingleFileUploader
              companyId={companyId}
              file={uploadFile as unknown as FileBasic}
              setFile={(next) => {
                if (typeof next === 'function') return;
                void onFileSet(next);
              }}
              accept={ACCEPT}
              maxSize={MAX_SIZE}
              documentType={LegacyDocumentType.Other}
              _className="w-full"
            />
            {isUploading && (
              <p className="txt-small-regular text-text-secondary">
                Adding document to service…
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label className="txt-small-medium text-text-secondary">
        Upload file
      </Label>
      <SingleFileUploader
        companyId={undefined}
        file={uploadFile as unknown as FileBasic}
        setFile={(next) => {
          if (typeof next === 'function') return;
          void onFileSet(next);
        }}
        onUploadStatusChange={onUploadStatusChange}
        accept={ACCEPT}
        maxSize={MAX_SIZE}
        documentType={LegacyDocumentType.Other}
        _className="w-full"
      />
      {isUploading && (
        <p className="txt-small-regular text-text-secondary">Uploading…</p>
      )}
    </div>
  );
}
